import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { TOUR_CONFIG } from './constants';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface TourSpotlightProps {
  targetSelector: string;
  padding?: number;
  isVisible: boolean;
  onClick?: () => void;
}

export function TourSpotlight({
  targetSelector,
  padding = 8,
  isVisible,
  onClick
}: TourSpotlightProps) {
  const [showEscapeHint, setShowEscapeHint] = useState(false);
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const [attempts, setAttempts] = useState(0);
  const retryTimeoutRef = useRef<number | null>(null);
  const debounceTimeoutRef = useRef<number | null>(null);
  const scrollRafRef = useRef<number | null>(null);

  const updateRect = useCallback(() => {
    const element = document.querySelector(targetSelector);
    if (element) {
      const bounds = element.getBoundingClientRect();
      setRect({
        top: bounds.top - padding,
        left: bounds.left - padding,
        width: bounds.width + padding * 2,
        height: bounds.height + padding * 2,
      });
      setAttempts(0);
    } else if (attempts < TOUR_CONFIG.MAX_RETRY_ATTEMPTS) {
      retryTimeoutRef.current = window.setTimeout(() => {
        setAttempts(prev => prev + 1);
      }, TOUR_CONFIG.RETRY_DELAY_MS);
    }
  }, [targetSelector, padding, attempts]);

  // Debounced scroll using rAF (prevents jank on iOS momentum scroll)
  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    scrollRafRef.current = requestAnimationFrame(updateRect);
  }, [updateRect]);

  const debouncedUpdateRect = useMemo(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      debounceTimeoutRef.current = window.setTimeout(updateRect, TOUR_CONFIG.MUTATION_DEBOUNCE_MS);
    };
  }, [updateRect]);

  useEffect(() => {
    if (!isVisible) {
      setRect(null);
      setAttempts(0);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return;
    }

    updateRect();

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', updateRect);

    const observer = new MutationObserver(debouncedUpdateRect);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', updateRect);
      observer.disconnect();
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, [targetSelector, padding, isVisible, attempts, updateRect, handleScroll, debouncedUpdateRect]);

  useEffect(() => {
    if (isVisible && !rect && attempts >= 5) {
      setShowEscapeHint(true);
    } else {
      setShowEscapeHint(false);
    }
  }, [isVisible, rect, attempts]);

  if (isVisible && !rect && attempts < TOUR_CONFIG.MAX_RETRY_ATTEMPTS) {
    return null;
  }

  return (
    <AnimatePresence>
      {isVisible && rect && (
        <>
          {/* Bottom navbar blocker — covers bottom navigation + safe area */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-0 left-0 right-0 z-[10000] pointer-events-none"
            style={{
              height: 'calc(80px + env(safe-area-inset-bottom, 0px))',
              background: 'rgba(0, 0, 0, 0.85)',
            }}
          />

          {/* Dark overlay with hole — tap to exit tour */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[10001] pointer-events-auto"
            onClick={onClick}
            style={{
              background: `radial-gradient(
                ellipse ${rect.width + 40}px ${rect.height + 40}px at ${rect.left + rect.width / 2}px ${rect.top + rect.height / 2}px,
                transparent 0%,
                transparent 70%,
                rgba(0, 0, 0, 0.85) 100%
              )`,
            }}
          />

          {/* Spotlight border/glow effect — elevated above overlay so target is visible */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="fixed z-[10002] pointer-events-none rounded-2xl"
            style={{
              top: rect.top,
              left: rect.left,
              width: rect.width,
              height: rect.height,
              boxShadow: '0 0 0 4px hsl(var(--primary)), 0 0 20px hsl(var(--primary) / 0.5)',
            }}
          >
            {/* Static border highlight (no infinite animation) */}
            <div className="absolute inset-0 rounded-2xl border-2 border-primary opacity-90" />
          </motion.div>
        </>
      )}

      {/* Escape hint when element not found */}
      {isVisible && showEscapeHint && !rect && (
        <motion.div
          initial={{ y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="fixed z-[10003] bg-card/95 backdrop-blur-sm border border-border rounded-xl px-4 py-3 shadow-lg pointer-events-auto"
          style={{
            bottom: 100,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100vw - 32px)',
          }}
          onClick={onClick}
        >
          <p className="text-sm text-muted-foreground text-center">
            Tap anywhere to exit the tour
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
